import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { formatErrorMessage } from '../../utils/errorFormatter';

const CompetencyMatrix = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [matrices, setMatrices] = useState([]);
  const [courses, setCourses] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMatrix, setEditingMatrix] = useState(null);

  const competencyDescriptions = {
    1: "Level 1: Basic/Novice - Has minimal or no knowledge; needs significant guidance and supervision to perform tasks.",
    2: "Level 2: Limited/Advanced Beginner - Understands basic concepts, can perform simple tasks with some support, but lacks deep context.",
    3: "Level 3: Competent/Intermediate - Can work independently on routine tasks, understands procedures, and applies knowledge effectively.",
    4: "Level 4: Proficient/Advanced - Has considerable experience, can handle complex situations, and often acts as a resource for others.",
    5: "Level 5: Expert/Master - Possesses deep, comprehensive knowledge, can innovate, troubleshoot complex issues, and train/mentor others."
  };

  const [matrixForm, setMatrixForm] = useState({
    role_name: '',
    required_courses: [],
    required_certifications: [],
    competency_level: 3
  });

  const [certTypeInput, setCertTypeInput] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-open edit modal when navigated with ?edit=ID from detail page
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (editId && matrices.length > 0) {
      const matrixToEdit = matrices.find(m => m.id === editId);
      if (matrixToEdit) {
        openModal(matrixToEdit);
        navigate('/training/competency', { replace: true });
      }
    }
  }, [matrices, location.search]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [matricesRes, coursesRes, certsRes] = await Promise.all([
        axios.get(`${API}/competency-matrix`, { headers }),
        axios.get(`${API}/training-courses`, { headers }),
        axios.get(`${API}/certifications`, { headers })
      ]);

      setMatrices(matricesRes.data);
      setCourses(coursesRes.data);
      setCertifications(certsRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      showError(formatErrorMessage(err, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  };

  const openModal = (matrix = null) => {
    if (matrix) {
      setEditingMatrix(matrix);
      setMatrixForm({
        role_name: matrix.role_name,
        required_courses: matrix.required_courses || [],
        required_certifications: matrix.required_certifications || [],
        competency_level: matrix.competency_level || 3
      });
    } else {
      setEditingMatrix(null);
      setMatrixForm({
        role_name: '',
        required_courses: [],
        required_certifications: [],
        competency_level: 3
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (editingMatrix) {
        await axios.put(
          `${API}/competency-matrix/${editingMatrix.id}`,
          matrixForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/competency-matrix`,
          matrixForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setShowModal(false);
      fetchData();
      showSuccess(editingMatrix ? 'Competency matrix updated successfully' : 'Competency matrix created successfully');
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to save competency matrix'));
    }
  };

  const toggleCourse = (courseId) => {
    setMatrixForm(prev => ({
      ...prev,
      required_courses: prev.required_courses.includes(courseId)
        ? prev.required_courses.filter(id => id !== courseId)
        : [...prev.required_courses, courseId]
    }));
  };

  // Derive unique certification names from the certifications in the system
  const availableCertNames = [...new Set(certifications.map(c => c.certificate_name).filter(Boolean))].sort();

  const toggleCertification = (certName) => {
    setMatrixForm(prev => ({
      ...prev,
      required_certifications: prev.required_certifications.includes(certName)
        ? prev.required_certifications.filter(c => c !== certName)
        : [...prev.required_certifications, certName]
    }));
  };

  const addCertType = () => {
    if (certTypeInput.trim() && !matrixForm.required_certifications.includes(certTypeInput.trim())) {
      setMatrixForm(prev => ({
        ...prev,
        required_certifications: [...prev.required_certifications, certTypeInput.trim()]
      }));
      setCertTypeInput('');
    }
  };

  const removeCertType = (certType) => {
    setMatrixForm(prev => ({
      ...prev,
      required_certifications: prev.required_certifications.filter(ct => ct !== certType)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/training')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Training Dashboard
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📊 Competency Matrix</h1>
              <p className="text-gray-600 mt-1">Define training requirements by role</p>
            </div>
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
              <button
                onClick={() => openModal()}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Role
              </button>
            )}
          </div>
        </div>

        {/* Matrices Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {matrices.map(matrix => (
            <div key={matrix.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/training/competency/${matrix.id}`)}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{matrix.role_name}</h3>
                  <p className="text-sm text-gray-500">Competency Level: {matrix.competency_level || 3}/5</p>
                </div>
                {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
                  <button
                    onClick={(e) => { e.stopPropagation(); openModal(matrix); }}
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Edit
                  </button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Required Training ({matrix.required_courses?.length || 0})</h4>
                  {matrix.required_courses && matrix.required_courses.length > 0 ? (
                    <ul className="space-y-1">
                      {matrix.required_courses.map(courseId => {
                        const course = courses.find(c => c.id === courseId);
                        return course ? (
                          <li key={courseId} className="text-sm text-gray-600 flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            {course.course_title}
                          </li>
                        ) : null;
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-400">No required courses</p>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700 mb-2">Required Certifications ({matrix.required_certifications?.length || 0})</h4>
                  {matrix.required_certifications && matrix.required_certifications.length > 0 ? (
                    <ul className="space-y-1">
                      {matrix.required_certifications.map(certType => (
                        <li key={certType} className="text-sm text-gray-600 flex items-center gap-2">
                          <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                          {certType}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-400">No required certifications</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {matrices.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No competency matrices defined</p>
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
              <button
                onClick={() => openModal()}
                className="mt-4 text-purple-600 hover:underline font-semibold"
              >
                Define requirements for your first role
              </button>
            )}
          </div>
        )}

        {/* Matrix Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingMatrix ? 'Edit Competency Matrix' : 'Add Role Requirements'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Role Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={matrixForm.role_name}
                    onChange={(e) => setMatrixForm({ ...matrixForm, role_name: e.target.value })}
                    placeholder="e.g., Supervisor, Forklift Operator"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                {/* Competency Level */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Competency Level (1-5)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={matrixForm.competency_level}
                    onChange={(e) => setMatrixForm({ ...matrixForm, competency_level: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                  {/* <p className="text-xs text-gray-500 mt-1">1 = No competency required, 5 = Expert level</p> */}
                  <p className="text-sm text-gray-600 mt-2">
                    {competencyDescriptions[matrixForm.competency_level] ||
                      "1 = Basic, 2 = Limited, 3 = Competent, 4 = Proficient, 5 = Expert"}
                  </p>
                </div>

                {/* Required Courses */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Required Training Courses</label>
                  <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                    {courses.map(course => (
                      <label key={course.id} className="flex items-center gap-2 hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={matrixForm.required_courses.includes(course.id)}
                          onChange={() => toggleCourse(course.id)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700">{course.course_title}</span>
                        <span className="text-xs text-gray-500">({course.training_type})</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{matrixForm.required_courses.length} course(s) selected</p>
                </div>

                {/* Required Certifications */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Required Certifications</label>
                  {availableCertNames.length > 0 ? (
                    <div className="border border-gray-300 rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                      {availableCertNames.map(certName => (
                        <label key={certName} className="flex items-center gap-2 hover:bg-gray-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={matrixForm.required_certifications.includes(certName)}
                            onChange={() => toggleCertification(certName)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-gray-700">{certName}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic mb-2">No certifications found in the system.</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{matrixForm.required_certifications.length} certification(s) selected</p>

                  {/* Custom certification name input */}
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Add custom certification not listed above</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={certTypeInput}
                        onChange={(e) => setCertTypeInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCertType(); } }}
                        placeholder="e.g., Forklift License"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <button
                        type="button"
                        onClick={addCertType}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Show custom (non-checkbox) selections */}
                  {matrixForm.required_certifications.filter(c => !availableCertNames.includes(c)).length > 0 && (
                    <div className="mt-2 space-y-2">
                      <p className="text-xs font-medium text-gray-500">Custom certifications:</p>
                      {matrixForm.required_certifications.filter(c => !availableCertNames.includes(c)).map(certType => (
                        <div key={certType} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                          <span className="text-sm text-gray-700">{certType}</span>
                          <button
                            onClick={() => removeCertType(certType)}
                            className="text-red-600 hover:text-red-800"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!matrixForm.role_name}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
                >
                  {editingMatrix ? 'Update Matrix' : 'Create Matrix'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default CompetencyMatrix;
